import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { ScrollReveal, SectionLabel, EASE } from "./Reveal";

// Swap the placeholder posters and stock clips for Jacques' real project footage when available.
const items = [
  {
    n: "01",
    title: "Brand Stories",
    desc: "Powerful films for businesses and brands that make an impact.",
    video: "/videos/lenjohnson.mp4",
    img: "/videos/lenjohnson-poster.jpg",
    result: "The true story of Len Johnson — a boxing pioneer Britain tried to forget.",
  },
  {
    n: "02",
    title: "Social Content",
    desc: "Short-form content designed for Instagram, TikTok and YouTube.",
    video: "/videos/burgers-reel.mp4",
    img: "/videos/burgers-poster.jpg",
    result: "Bookings jumped within 48 hours of it dropping — and they booked again.",
  },
  {
    n: "03",
    title: "Events",
    desc: "Capture the atmosphere, energy and moments that matter.",
    video: "/videos/wedding-3.mp4",
    img: "/videos/wedding-3-poster.jpg",
    result: "One couple, one day — every moment that mattered, kept forever.",
  },
  {
    n: "04",
    title: "Personal Stories",
    desc: "Documentaries, journeys and real-life stories.",
    video: "/videos/personal-story.mp4",
    img: "/videos/personal-story-poster.jpg",
    result: "A real life, filmed honestly — no script, just story.",
  },
];

export const WhatIDoSection = () => {
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);
  const touchX = useRef(null);

  const step = useCallback((dir) => {
    setProgress(0);
    setActive((cur) => {
      if (!cur) return cur;
      const i = items.findIndex((it) => it.n === cur.n);
      return items[(i + dir + items.length) % items.length];
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, step]);

  return (
  <section id="work" data-testid="what-i-do-section" className="py-24 lg:py-32">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <SectionLabel index="01" title="What I Do — Selected Work" />
      </ScrollReveal>

      <div className="border-t border-white/10">
        {items.map((item, i) => (
          <ScrollReveal key={item.n} delay={i * 0.06}>
            <button
              data-testid={`work-row-${item.n}`}
              onClick={() => { setProgress(0); setActive(item); }}
              className="group w-full text-left border-b border-white/10 py-8 lg:py-10 px-2 lg:px-6 grid grid-cols-12 gap-4 items-center hover:bg-[#111111] transition-colors duration-500"
            >
              <span className="col-span-2 sm:col-span-1 font-mono2 text-xs tracking-[0.3em] text-[#8A8A85]">{item.n}</span>
              <h3 className="col-span-10 sm:col-span-5 font-display text-3xl sm:text-4xl lg:text-5xl leading-none group-hover:translate-x-2 transition-transform duration-500">
                {item.title}
              </h3>
              <div className="col-span-10 col-start-3 sm:col-span-3 sm:col-start-7">
                <p className="text-sm text-[#8A8A85] leading-relaxed">{item.desc}</p>
                {item.result && (
                  <p data-testid={`work-result-${item.n}`} className="mt-3 flex items-start gap-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#F2F0EB] leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F2F0EB] mt-1 shrink-0" />
                    {item.result}
                  </p>
                )}
              </div>
              <div className="col-span-10 col-start-3 sm:col-span-2 sm:col-start-10 mt-4 sm:mt-0">
                <div className="relative aspect-video overflow-hidden border border-white/10" data-testid={`work-thumb-${item.n}`}>
                  <video
                    src={item.video || undefined}
                    poster={item.img}
                    muted
                    loop
                    playsInline
                    autoPlay={Boolean(item.video)}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-[transform,filter] duration-700"
                  />
                  <span
                    data-testid={`work-play-badge-${item.n}`}
                    className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] text-[#F2F0EB] pointer-events-none"
                  >
                    <Play className="w-2.5 h-2.5 fill-current" />
                    Tap to play
                  </span>
                </div>
              </div>
              <span className="hidden sm:flex col-span-1 justify-end">
                <ArrowRight className="w-6 h-6 text-[#8A8A85] group-hover:translate-x-2 group-hover:text-[#F2F0EB] transition-all duration-500" />
              </span>
            </button>
          </ScrollReveal>
        ))}
      </div>
    </div>

    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="work-cinema-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-0 z-[90] bg-[#0A0A0A]/95 backdrop-blur-xl flex flex-col items-center justify-center p-6"
          onClick={() => setActive(null)}
          onTouchStart={(e) => { touchX.current = e.touches?.[0]?.clientX ?? null; }}
          onTouchEnd={(e) => {
            const x = e.changedTouches?.[0]?.clientX;
            if (touchX.current === null || x === undefined) { touchX.current = null; return; }
            const dx = x - touchX.current;
            if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}
        >
          <button
            data-testid="work-cinema-close-btn"
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="work-cinema-prev-btn"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="work-cinema-next-btn"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-4 lg:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-[#8A8A85] mb-6">
            {active.n} — {active.title}
          </p>
          <motion.div
            initial={{ scale: 0.94, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 20 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key={active.n}
              data-testid="work-cinema-video"
              src={active.video}
              poster={active.img}
              controls
              autoPlay
              playsInline
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (v.duration) setProgress((v.currentTime / v.duration) * 100);
              }}
              className="w-full aspect-video border border-white/10 bg-black"
            />
            <div className="mt-3 h-[3px] w-full bg-white/10 overflow-hidden" data-testid="work-cinema-progress">
              <div
                data-testid="work-cinema-progress-fill"
                className="h-full bg-[#F2F0EB] transition-[width] duration-200 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-4 text-sm text-[#8A8A85] text-center">{active.desc}</p>
            {active.result && (
              <p data-testid="work-cinema-result" className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F2F0EB] text-center">
                {active.result}
              </p>
            )}
            <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] text-center">
              ← → or swipe to browse · Esc to close
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};
